import { useState, type Dispatch, type SetStateAction } from "react"

import { useOrganization } from "@clerk/nextjs"

import { useOptionalWorkspaceContext } from "../contexts/WorkspaceContext"

/**
 * Row selection for a list, cleared whenever the organization, workspace, or
 * caller-defined collection scope it was made in changes.
 *
 * Selected ids only mean something inside the scope they were picked in.
 * Switching organizations swaps the whole list out from under the selection,
 * so a selection carried across would leave the bulk bar counting rows that
 * are no longer on screen and its tag and delete actions addressing records
 * this organization cannot reach. A caller whose server-filtered catalog can
 * change in place supplies that filter as `collectionScopeKey` for the same
 * reason.
 *
 * Returns the `useState` pair, so a list holds and clears its selection the
 * way it otherwise would.
 */
export function useScopedSelection(
  collectionScopeKey?: string,
): [Set<string>, Dispatch<SetStateAction<Set<string>>>] {
  const { organization } = useOrganization()
  const workspaceContext = useOptionalWorkspaceContext()
  const scopeId = JSON.stringify([
    organization?.id ?? null,
    workspaceContext?.currentWorkspace?.id ?? null,
    collectionScopeKey ?? null,
  ])

  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set<string>(),
  )
  const [selectionScopeId, setSelectionScopeId] = useState(scopeId)

  // Adjusted during render rather than in an effect so the scope change and
  // the empty selection reach the bulk bar in the same commit — an effect
  // would paint the stale count and its destructive controls first.
  if (selectionScopeId !== scopeId) {
    setSelectionScopeId(scopeId)
    setSelectedIds(new Set<string>())
  }

  return [selectedIds, setSelectedIds]
}
